'use client'

import CloseButton from '@component/closeButton'
import DisablePrefetchLink from '@component/disablePrefetchLink'
import ImageBgLoading from '@component/imageBgLoading'
import MaskLoading from '@component/loading/mask'
import Menu from '@component/menu'
import useAddOpenhome from '@hooks/useAddOpenhome'
import useEnquiry from '@hooks/useEnquiry'
import useFetch from '@hooks/useFetch'
import IconApple from '@icon/const/logo-apple.svg'
import IconGoogle from '@icon/const/logo-google.svg'
import IconOutlook from '@icon/const/logo-outlook.svg'
import IconCalendar from '@icon/house/calendar.svg'
import { downloadAppleIcs, downloadGoogle, downloadOutlookIcs } from '@plugin/calendar'
import { auctionDataLayer, enquiryDataLayer, openhomeDataLayer } from '@plugin/google/gtm/modules/property'
import { URLS, getRouterHref, houseDetail } from '@router'
import { getFullPath, getTime, joinStr, toNumber } from '@utils'
import { getHouseId, isNewhomeData, isRentData, isSaleData, isVillageData } from '@utils/house'
import classNames from 'classnames'
import { first, get, join, map, slice, take } from 'lodash-es'
import { useCallback, useEffect, useMemo, useState } from 'react'

export default function AddOpenHomeContent() {

  const { house, openhome, auction, online, hideAddOpenhome, toggleLoading } = useAddOpenhome()

  // 保存到日历
  const type = (() => {
    let type = openhome ? 2 : 3
    if (online) type = openhome ? 4 : 5
    return type
  })()
  const fetchKey = useMemo(() => Date.now(), [])
  const { data, isLoading } = useFetch(
    {
      url: 'user/appointment',
      params: {
        type,
        start: get(openhome || auction, 'start'),
        end: get(openhome || auction, 'end'),
        houseId: getHouseId(house)
      },
      method: 'post',
      fetchKey
    },
    {
      onSuccess() {
        if (openhome) openhomeDataLayer.success()
        if (auction) auctionDataLayer.success()
      }
    }
  )

  const homeInsuranceUrl = useMemo(() => {
    const refPrice = toNumber(get(house, 'priceSearch'))
    let res = 'https://assets.oneroof.co.nz/file/aa/%20i/aa%20insurance%20checklist%20insurance%20101.pdf'
    // is new homes
    if (isNewhomeData(house)) {
      res = 'https://assets.oneroof.co.nz/file/aa/%20i/aa%20insurance%20checklist%20new%20build.pdf'
    }
    // residential && Home & Income
    else if (isSaleData(house) && house.categoryId === 6){
      res = 'https://assets.oneroof.co.nz/file/aa/%20i/aa%20insurance%20checklist%20home%20&%20income.pdf'
    } else if (refPrice) {
      if (refPrice >= 2000000) {
        res = 'https://assets.oneroof.co.nz/file/aa/%20i/aa%20insurance%20checklist%20home%20&%20contents.pdf'
      } else if (refPrice < 1000000 && refPrice > 0) {
        res = 'https://assets.oneroof.co.nz/file/aa/%20i/aa%20insurance%20checklist%20first%20home%20buyer.pdf'
      }
    }
    return res
  }, [house])

  // 更新loading状态
  useEffect(() => {
    toggleLoading(isLoading)
  }, [isLoading, toggleLoading])

  return (
    <div className='relative max-w-full flex flex-col gap-y-10 py-20 px-12 sm:gap-y-20 sm:px-20 sm:pt-40 sm:pb-35 sm:w-[600px]'>
      <CloseButton className='absolute z-10 right-10 top-10' onClick={hideAddOpenhome}></CloseButton>
      <div className='flex flex-col items-center justify-center gap-x-8 sm:flex-row'>
        <div className='w-40 h-40 rounded-half bg-secondary grid place-items-center sm:w-30 sm:h-30'>
          <i className='icon icon-correct text-white !text-2xl font-bold sm:!text-lg'></i>
        </div>
        <span className='text-2xl font-medium mt-6 sm:mt-0'>Added to your</span>
        <DisablePrefetchLink
          href={getRouterHref(URLS.profileJourney, undefined, { start: get(openhome || auction, 'start') })}
          className='button-text-primary text-2xl hover:underline'
        >
          OneRoof planner
          <i className='icon icon-arrow-right !text-xl'></i>
        </DisablePrefetchLink>
      </div>
      <HouseCard />
      <Information />
      <MaskLoading loading={isLoading || !data} className="!z-20"></MaskLoading>
    </div>
  )
}

function HouseCard() {

  const { house, openhome, auction } = useAddOpenhome()
  // event data
  const eventData = useMemo(() => {
    const data = openhome || auction
    const { teaser } = house
    const houseUrl = getFullPath(houseDetail(house))
    const journeyUrl = getFullPath(getRouterHref(URLS.profileJourney))

    let event = {}
    event.start = new Date(data.start * 1000)
    event.end = data.end ? new Date(data.end * 1000) : null
    event.timezone = 'Pacific/Auckland'
    event.title = ['Open home for', teaser].join(' ')

    event.description = [
      join(take(get(house, 'description'), 150), ''),
      `for more information, please click <a href="${houseUrl}" target="_blank">${teaser}</a>`,
      `To view your open home planner, please click ${journeyUrl}`
    ].join('\r\n')
    event.location = joinStr([house.street, house.suburb], ', ')
    event.all_day_event = !data.end

    return event
  }, [openhome, auction, house])
  const { loadings, downloadApple, downloadOutlook } = useCalendar(eventData)

  const calendarMenu = [
    <Item text='Google calendar' onClick={() => downloadGoogle(eventData, getFullPath(houseDetail(house)))} key="google">
      <IconGoogle className='w-24 h-24' />
    </Item>,
    <Item text='Apple calendar' onClick={downloadApple} key="apple" className='border-t'>
      <IconApple className='w-28 h-28' />
    </Item>,
    <Item text='Outlook calendar' onClick={downloadOutlook} key="outlook" className='border-t'>
      <IconOutlook className='w-24 h-24' />
    </Item>
  ]

  return (
    <div className='shadow-md rounded'>
      <ImageBgLoading src={get(house, 'images.0')} width={561} height={170} className='object-cover w-full h-148 sm:h-170 block rounded-t' alt={house.address}></ImageBgLoading>
      <div className='bg-font p-12 text-center'>
        {
          Boolean(openhome) && (
            <>
              <div className='text-white flex flex-col sm:flex-row sm:items-center sm:justify-center sm:flex-wrap gap-x-10'>
                <span className='text-xl'>Open Home:</span>
                <span className='text-lg'>{joinStr([house.street, house.suburb], ', ')}</span>
              </div>
              <div className='text-secondary text-lg'>
                {joinStr([getTime(openhome.start, 'ddd DD MMM, hh.mmA'), getTime(openhome.end, 'hh.mmA')], ' - ')}
              </div>
            </>
          )
        }
        {
          Boolean(auction) && (
            <>
              <div className='text-white text-xl'>Auction: {joinStr([house.street, house.suburb], ', ')}</div>
              <div className='text-secondary text-lg'>
                {getTime(auction.start, 'ddd DD MMM, hh.mmA')}
              </div>
            </>
          )
        }
      </div>
      <div className='p-15 text-center'>
        <Menu data={calendarMenu} width={240} className="inline-block">
          <button className='button-text-primary inline-flex items-center justify-center gap-x-8 !text-lg relative'>
            Add to personal calendar
            <IconCalendar className='fill-primary w-20 h-20' />
            <MaskLoading loading={loadings.apple || loadings.outlook}></MaskLoading>
          </button>
        </Menu>
      </div>
    </div>
  )
}

function Item({
  children,
  text,
  onClick,
  className = '',
}) {

  return (
    <div className={classNames('hover:bg-primary-light', className)}>
      <button className='h-50 text-sm flex items-center pl-35' onClick={onClick}>
        {children}
        <span className='ml-10 text-lg'>{text}</span>
      </button>
    </div>
  )
}

function Information() {

  const { house } = useAddOpenhome()
  const { data, isLoading } = useFetch({
    url: 'enum/enquiry-specs',
    params: (() => {
      let type
      let id = get(house, 'houseId')
      if (isVillageData(house)) {
        type = 'village'
      } else if (isRentData(house)) {
        type = 'rental'
      } else {
        type = 'house'
      }
      return { type, id }
    })()
  })

  if (isLoading) {
    return (
      <div className='skeleton-wrap space-y-10'>
        <span className='h-20'></span>
        <span className='h-20'></span>
        <span className='h-20'></span>
      </div>
    )
  }

  return (
    <div className='flex gap-x-20 gap-y-10 flex-col sm:flex-row mt-16'>
      <div className='text-xl text-center sm:text-left'>Want more information?</div>
      <div className='flex-1 min-w-0 space-y-10 sm:space-y-18'>
        {
          map(slice(data, 1, 3), (item, idx) => (
            <InformationItem key={idx} data={item} />
          ))
        }
      </div>
    </div>
  )
}
function InformationItem({ data }) {

  const { house, hideAddOpenhome } = useAddOpenhome()
  const { showHouseEnquiry } = useEnquiry()
  const requestHandle = useCallback(event => {
    hideAddOpenhome()
    enquiryDataLayer.click(data)
    showHouseEnquiry({
      house: house,
      agent: first(house.agents),
      signupSource: 'agent_enquiry',
      spec: data,
      sendAllAgent: true
    })(event)
  }, [showHouseEnquiry, data, house, hideAddOpenhome])

  const label = useMemo(() => {
    switch (data.value) {
      case 'more_information':
        return 'Learn more about this property'
      case 'house_inspection':
      case 'price_indication':
        return 'Get a price indication'
      default:
        return data.label
    }
  }, [data])

  return (
    <div className='button-primary-out !px-20 w-full rounded-full justify-between !h-48 !text-base' onClick={requestHandle}>
      {label}
      <i className='icon icon-arrow-right'></i>
    </div>
  )
}

function useCalendar(eventData) {

  const [loadings, setLoadings] = useState({
    apple: false,
    outlook: false,
  })
  const downloadApple = useCallback(() => {
    setLoadings(d => ({ ...d, apple: true }))
    downloadAppleIcs(eventData).finally(() => setLoadings(d => ({ ...d, apple: false })))
  }, [eventData, setLoadings])

  const downloadOutlook = useCallback(() => {
    setLoadings(d => ({ ...d, outlook: true }))
    downloadOutlookIcs(eventData).finally(() => setLoadings(d => ({ ...d, outlook: false })))
  }, [eventData, setLoadings])

  return {
    loadings,
    downloadApple,
    downloadOutlook
  }
}